<template>
  <div class="sales-page flex w-full h-screen overflow-hidden bg-gray-100">
    <div class="flex-1 w-full overflow-hidden">
      <Topbar />

      <div class="mt-[64px] h-[calc(100vh-64px)] overflow-hidden px-2 py-3 md:px-3 md:py-4 lg:px-4">
        <Toast />
        <div class="grid h-full grid-cols-1 gap-3 overflow-hidden lg:grid-cols-12">
          <section class="flex min-h-0 flex-col rounded-2xl border border-gray-200 bg-white p-2.5 md:p-3 lg:col-span-7 xl:col-span-8">
            <div class="mb-2.5 shrink-0 flex flex-wrap items-center gap-2">
              <Button
                label="Товары"
                icon="pi pi-box"
                :severity="activeTab === 'products' ? 'success' : 'secondary'"
                :outlined="activeTab !== 'products'"
                size="small"
                @click="activeTab = 'products'"
              />
              <Button
                label="Витрины"
                icon="pi pi-shop"
                :severity="activeTab === 'showcases' ? 'success' : 'secondary'"
                :outlined="activeTab !== 'showcases'"
                size="small"
                @click="activeTab = 'showcases'"
              />
              <Button
                label="Частые"
                icon="pi pi-star"
                :severity="activeTab === 'frequent' ? 'success' : 'secondary'"
                :outlined="activeTab !== 'frequent'"
                size="small"
                @click="activeTab = 'frequent'"
              />
              <Button
                label="Отложенные"
                icon="pi pi-bookmark"
                :severity="activeTab === 'held' ? 'success' : 'secondary'"
                :outlined="activeTab !== 'held'"
                size="small"
                @click="activeTab = 'held'"
              />
              <div class="ml-auto flex items-center gap-2">
                <Button
                  icon="pi pi-th-large"
                  label="Сетка"
                  :severity="catalogView === 'grid' ? 'success' : 'secondary'"
                  :outlined="catalogView !== 'grid'"
                  size="small"
                  @click="catalogView = 'grid'"
                />
                <Button
                  icon="pi pi-bars"
                  label="Таблица"
                  :severity="catalogView === 'table' ? 'success' : 'secondary'"
                  :outlined="catalogView !== 'table'"
                  size="small"
                  @click="catalogView = 'table'"
                />
              </div>
            </div>

            <IconField class="mb-2.5 w-full shrink-0">
              <InputIcon><i class="pi pi-search" /></InputIcon>
              <InputText v-model.trim="searchQuery" class="w-full text-sm" placeholder="Поиск товара по названию или штрихкоду..." />
            </IconField>

            <div v-if="activeTab === 'products'" class="mb-2 flex shrink-0 flex-wrap gap-2">
              <Button
                v-for="chip in productCategoryChips"
                :key="chip"
                :label="chip"
                :severity="selectedProductCategory === chip ? 'success' : 'secondary'"
                :outlined="selectedProductCategory !== chip"
                size="small"
                class="!rounded-xl"
                @click="selectedProductCategory = chip"
              />
            </div>

            <div v-if="activeTab === 'showcases' && showcaseTypeChips.length > 1" class="mb-2 flex shrink-0 flex-wrap gap-2">
              <Button
                v-for="chip in showcaseTypeChips"
                :key="`showcase-type-${chip.id ?? 'all'}`"
                :label="chip.label"
                :severity="selectedShowcaseProductTypeId === chip.id ? 'success' : 'secondary'"
                :outlined="selectedShowcaseProductTypeId !== chip.id"
                size="small"
                class="!rounded-xl"
                @click="selectedShowcaseProductTypeId = chip.id"
              />
            </div>

            <div v-if="activeTab === 'products' && selectedProductCategory !== 'Все' && productSubcategoryChips.length > 1" class="mb-2.5 flex shrink-0 flex-wrap gap-2">
              <Button
                v-for="chip in productSubcategoryChips"
                :key="`subcategory-${chip}`"
                :label="chip"
                :severity="selectedProductSubcategory === chip ? 'success' : 'secondary'"
                :outlined="selectedProductSubcategory !== chip"
                size="small"
                class="!rounded-xl"
                @click="selectedProductSubcategory = chip"
              />
            </div>

            <div v-if="loading" class="flex flex-1 items-center justify-center text-sm text-gray-500">Загрузка...</div>

            <div v-else class="min-h-0 flex-1">
              <div
                v-if="catalogView === 'grid'"
                class="catalog-grid grid h-full auto-rows-max content-start items-start overflow-y-auto pr-1 grid-cols-1 gap-2.5 sm:grid-cols-2 md:grid-cols-4 md:gap-3 xl:grid-cols-4"
              >
                <article
                  v-for="item in visibleItems"
                  :key="`${activeTab}-${item.id}`"
                  class="flex self-start cursor-pointer flex-col rounded-xl border border-gray-200 bg-gray-50 p-2.5 transition-colors hover:border-green-300 md:p-3"
                  @click="onCatalogItemClick(item)"
                >
                  <div class="mb-1 flex justify-end">
                    <Button
                      v-if="item.type === 'showcase'"
                      icon="pi pi-list"
                      text
                      rounded
                      size="small"
                      severity="secondary"
                      @click.stop="openShowcaseDetails(item)"
                    />
                    <Button
                      :icon="isFrequent(item.key) ? 'pi pi-star-fill' : 'pi pi-star'"
                      text
                      rounded
                      size="small"
                      :severity="isFrequent(item.key) ? 'contrast' : 'secondary'"
                      :class="isFrequent(item.key) ? 'favorite-star-active' : ''"
                      @click.stop="toggleFrequent(item)"
                    />
                  </div>
                  <div class="flex h-16 items-center justify-center overflow-hidden md:h-20">
                    <img v-if="item.image" :src="item.image" :alt="item.name" class="max-h-full max-w-full object-contain" />
                    <i v-else class="pi pi-image text-xl text-gray-300 md:text-2xl"></i>
                  </div>

                  <p class="mt-1.5 min-h-[2rem] line-clamp-2 text-center text-sm font-semibold md:text-md">{{ item.name }}</p>
                  <div v-if="item.type === 'product'" class="flex min-h-[1.5rem] flex-wrap items-center justify-center gap-1">
                    <span
                      v-for="code in displayBarcodes(item.barcodes)"
                      :key="`${item.id}-${code}`"
                      class="rounded bg-gray-100 px-1 py-1 text-xs text-gray-700"
                    >
                      {{ code }}
                    </span>
                    <span
                      v-if="hiddenBarcodesCount(item.barcodes) > 0"
                      class="rounded bg-green-100 px-1 py-1 text-xs font-medium text-green-700"
                    >
                      +{{ hiddenBarcodesCount(item.barcodes) }}
                    </span>
                  </div>
                  <div v-if="item.type === 'showcase'" class="flex min-h-[1.5rem] items-center justify-center">
                    <span class="truncate rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">
                      {{ item.barcode || 'Штрихкод не задан' }}
                    </span>
                  </div>
                  <p
                    v-if="item.type === 'product' && item.expiryDateLabel"
                    class="mt-1 text-center text-[11px] text-slate-500"
                  >
                    Срок годности: {{ item.expiryDateLabel }}
                  </p>
                  <div v-if="item.type === 'held'" class="mt-2 flex min-h-[1.5rem] justify-center">
                    <span class="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-700">
                      {{ item.createdAt }}
                    </span>
                  </div>
                  <div class="flex items-end justify-between gap-1">
                    <p class="text-xs text-gray-500 md:text-sm">
                      {{ item.type === 'held' ? `Позиций: ${item.stock}` : `Ост: ${item.stock}` }}
                    </p>
                    <p class="text-right text-base font-bold text-green-700 md:text-sm">{{ item.price }} ₸</p>
                  </div>
                </article>
              </div>

              <div v-else class="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white">
                <div class="grid shrink-0 grid-cols-[minmax(0,2fr)_140px_100px_140px_120px] gap-3 border-b border-gray-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <div>Название</div>
                  <div>Штрихкод</div>
                  <div>Остаток</div>
                  <div>Цена</div>
                  <div class="text-right">Действие</div>
                </div>

                <div v-if="!visibleItems.length" class="flex-1 px-4 py-10 text-center text-sm text-slate-400">
                  Ничего не найдено
                </div>

                <div v-else class="min-h-0 flex-1 overflow-y-auto divide-y divide-gray-100">
                  <div
                    v-for="item in visibleItems"
                    :key="`table-${activeTab}-${item.id}`"
                    class="grid grid-cols-[minmax(0,2fr)_140px_100px_140px_120px] items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50"
                  >
                    <div class="min-w-0">
                      <div class="flex items-center gap-3">
                        <div class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100">
                          <img v-if="item.image" :src="item.image" :alt="item.name" class="h-full w-full object-contain" />
                          <i v-else class="pi pi-image text-sm text-slate-400"></i>
                        </div>
                        <div class="min-w-0">
                          <p class="truncate text-sm font-semibold text-slate-900">{{ item.name }}</p>
                          <p class="truncate text-xs text-slate-500">
                            {{ item.type === 'showcase' ? 'Витрина' : 'Товар' }}
                            <span v-if="item.type === 'product' && item.expiryDateLabel"> · Срок: {{ item.expiryDateLabel }}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    <div class="truncate text-sm text-slate-600">
                      {{ item.type === 'product' ? (item.barcode || '—') : item.type === 'showcase' ? (item.barcode || '—') : item.type === 'held' ? item.createdAt : '—' }}
                    </div>

                    <div class="text-sm font-medium text-slate-700">{{ item.stock }}</div>
                    <div class="text-sm font-semibold text-green-700">{{ item.price }} ₸</div>

                    <div class="flex justify-end gap-2">
                      <Button
                        v-if="item.type !== 'held'"
                        :icon="isFrequent(item.key) ? 'pi pi-star-fill' : 'pi pi-star'"
                        size="small"
                        :severity="isFrequent(item.key) ? 'contrast' : 'secondary'"
                        :class="isFrequent(item.key) ? 'favorite-star-active' : ''"
                        outlined
                        @click.stop="toggleFrequent(item)"
                      />
                      <Button
                        v-if="item.type === 'showcase'"
                        icon="pi pi-list"
                        size="small"
                        severity="secondary"
                        outlined
                        @click.stop="openShowcaseDetails(item)"
                      />
                      <Button
                        icon="pi pi-plus"
                        :label="item.type === 'held' ? 'Восстановить' : ''"
                        size="small"
                        severity="success"
                        @click="onCatalogItemClick(item)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <aside class="flex min-h-0 flex-col rounded-2xl border border-gray-200 bg-white p-2.5 md:p-3 lg:col-span-5 lg:h-full xl:col-span-4">
            <div class="mb-2.5 flex shrink-0 items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <i class="pi pi-receipt text-lg md:text-xl"></i>
                <h3 class="text-xl md:text-2xl font-semibold">Чек</h3>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  label="Доп. работа"
                  icon="pi pi-briefcase"
                  size="small"
                  severity="success"
                  outlined
                  @click="addExtraWorkLine"
                />
                <Button
                  label="Отложить"
                  icon="pi pi-bookmark"
                  size="small"
                  severity="secondary"
                  outlined
                  :disabled="!cartItems.length"
                  @click="holdCart"
                />
                <Button
                  label="Очистить"
                  icon="pi pi-trash"
                  size="small"
                  severity="danger"
                  outlined
                  :disabled="!cartItems.length"
                  @click="clearCart"
                />
              </div>
            </div>

            <Select
              v-model="selectedClient"
              :options="clientsWithGuest"
              optionLabel="label"
              filter
              :filterFields="['label', 'phone']"
              placeholder="— Без клиента —"
              class="w-full mb-2.5 text-sm"
              showClear
              @filter="handleClientFilter"
              @change="resetClientFilterState"
              @clear="resetClientFilterState"
              @hide="resetClientFilterState"
            >
              <template #header>
                <button
                  type="button"
                  class="flex w-full items-center gap-2 border-b border-slate-200 bg-emerald-500/8 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-emerald-500/12 cursor-pointer"
                  @click="openAddClientDialog"
                >
                  <i class="pi pi-plus-circle text-sm text-emerald-600" aria-hidden="true" />
                  <span>Добавить клиента</span>
                </button>
              </template>
              <template #option="slotProps">
                <div class="flex items-center justify-between gap-2 w-full">
                  <span class="truncate">{{ slotProps.option.label }}</span>
                  <span v-if="slotProps.option.id" class="text-xs text-gray-500 shrink-0">Бонус: {{ slotProps.option.bonus }} ₸</span>
                </div>
              </template>
              <template #emptyfilter>
                <div class="px-3 py-4 text-center text-sm text-slate-500">
                  Клиент не найден
                </div>
              </template>
            </Select>

            <div class="mb-2.5 rounded-lg border border-green-100 bg-green-50 px-2 py-1.5 flex items-center justify-between">
              <span class="text-xs text-green-700">Бонус клиента</span>
              <span class="text-sm font-semibold text-green-700">{{ selectedClientBonus }} ₸</span>
            </div>

            <div class="flex-1 overflow-y-auto overflow-x-hidden border-y border-gray-100 py-2 space-y-2 pr-1">
              <div v-if="!cartItems.length" class="h-full flex items-center justify-center text-gray-400 text-sm text-center">
                Добавьте товары в чек
              </div>

              <div
                v-for="item in cartItems"
                :key="item.key"
                class="group rounded-2xl border px-3 py-2 shadow-sm cursor-pointer transition-colors"
                :class="item.type === 'showcase'
                  ? 'border-blue-200 bg-blue-50/70 hover:bg-blue-50'
                  : item.type === 'extra-work'
                    ? 'border-emerald-200 bg-emerald-50/80 hover:bg-emerald-50'
                    : 'border-slate-200 bg-slate-50 hover:bg-slate-100'"
              >
                <div class="flex items-center gap-1.5 w-full">
                  <div class="flex min-w-0 w-[30%] flex-col justify-center gap-1 overflow-hidden">
                    <div class="min-w-0 flex items-center gap-2">
                      <Button
                        v-if="item.type === 'showcase'"
                        icon="pi pi-list"
                        text
                        severity="secondary"
                        size="small"
                        rounded
                        class="shrink-0"
                        @click="openCartCompositionDialog(item)"
                      />
                      <p
                        class="block min-w-0 truncate text-left text-[13px] font-semibold leading-5 text-slate-900"
                        :title="getCartItemDisplayName(item)"
                        v-tooltip.top="getCartItemDisplayName(item)"
                      >
                        {{ getCartItemDisplayName(item) }}
                      </p>
                    </div>
                    <div class="min-w-0">
                      <div class="flex items-center gap-1">
                        <button
                          v-if="editingPriceKey !== item.key"
                          type="button"
                          class="shrink-0 rounded-xl px-1.5 py-0.5 text-left text-xs font-medium transition-colors"
                          :class="item.unitPrice !== item.basePrice ? 'cart-price-custom' : 'text-slate-700 hover:bg-white'"
                          @click="startPriceEdit(item)"
                        >
                          {{ item.unitPrice }} ₸
                        </button>
                        <input
                          v-else
                          v-model="priceDraftValue"
                          type="number"
                          min="0"
                          step="1"
                          class="h-7 w-18 shrink-0 rounded-xl border border-slate-200 bg-white px-2 text-left text-xs font-medium text-slate-700 outline-none"
                          @blur="applyPriceEdit(item)"
                          @keydown.enter.prevent="applyPriceEdit(item)"
                          @keydown.esc.prevent="cancelPriceEdit"
                        >
                        <button
                          v-if="item.unitPrice !== item.basePrice"
                          type="button"
                          class="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--accent)] transition-colors hover:bg-white"
                          @click="resetPrice(item)"
                        >
                          ↺
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="flex w-[42%] shrink-0 items-center justify-center text-slate-900">
                    <div
                      v-if="item.type === 'extra-work' && item.autoAdjustment"
                      class="flex h-9 min-w-[12.5rem] items-center justify-center rounded-[12px] border border-emerald-200 bg-white/90 px-2.5 text-sm font-semibold text-emerald-700 shadow-sm"
                    >
                      Фиксированная строка
                    </div>
                    <Select
                      v-else-if="item.type === 'extra-work'"
                      v-model="item.extra_work_reference_id"
                      :options="extraWorkReferenceOptions"
                      optionLabel="name"
                      optionValue="id"
                      placeholder="Выберите доп. работу"
                      class="w-full min-w-[12.5rem]"
                      @update:modelValue="setExtraWorkReference(item, $event)"
                    />
                    <div
                      v-else
                      class="flex h-9 min-w-[12.5rem] items-center justify-between rounded-[12px] border border-stone-300 bg-white px-2.5 shadow-sm"
                    >
                      <button
                        type="button"
                        class="cart-stepper-action"
                        @click="decrementCartItem(item)"
                      >
                        −
                      </button>
                      <input
                        v-model="item.quantity"
                        type="number"
                        min="1"
                        :max="item.maxQty || 1"
                        step="1"
                        class="mx-2 h-7 w-24 rounded-lg border border-slate-200 bg-white px-2 text-center text-sm font-semibold leading-none text-slate-900 outline-none"
                        @blur="clampQuantity(item)"
                        @keydown.enter.prevent="clampQuantity(item)"
                      >
                      <button
                        type="button"
                        class="cart-stepper-action"
                        @click="incrementCartItem(item)"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div class="flex w-[30%] shrink-0 items-center justify-end gap-2">
                    <p class="text-right text-sm font-semibold tracking-tight text-slate-900">{{ getRoundedLineTotal(item) }} ₸</p>
                    <Button
                      icon="pi pi-trash"
                      text
                      severity="danger"
                      size="small"
                      rounded
                      class="opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                      @click="removeFromCart(item.key)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="pt-2.5 space-y-2.5">
              <div class="flex items-center justify-between text-2xl md:text-3xl font-semibold">
                <span>Итого:</span>
                <div class="flex items-center gap-2">
                  <button
                    v-if="!editingTotal"
                    type="button"
                    class="rounded-xl px-2 py-1 text-right text-green-700 transition-colors"
                    :class="isCustomTotal ? 'cart-price-custom' : 'hover:bg-white'"
                    @click="startTotalEdit"
                  >
                    {{ totalAmount }} ₸
                  </button>
                  <input
                    v-else
                    v-model="totalDraftValue"
                    type="number"
                    min="0"
                    step="1"
                    class="h-10 w-32 rounded-xl border border-slate-200 bg-white px-3 text-right text-lg font-semibold text-slate-900 outline-none"
                    @blur="applyTotalEdit"
                    @keydown.enter.prevent="applyTotalEdit"
                    @keydown.esc.prevent="cancelTotalEdit"
                  >
                  <button
                    v-if="isCustomTotal"
                    type="button"
                    class="inline-flex h-7 w-7 items-center justify-center rounded-full text-[var(--accent)] transition-colors hover:bg-white"
                    @click="resetTotalPrice"
                  >
                    ↺
                  </button>
                </div>
              </div>

              <div
                v-if="isCustomTotal"
                class="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700"
              >
                <span>Добавлено как "Доп. работа"</span>
                <span class="font-semibold">{{ totalDeltaLabel }}</span>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <div class="flex items-center gap-2 col-span-2">
                  <input
                    id="use-bonus-in-check"
                    v-model="useClientBonuses"
                    type="checkbox"
                    class="h-4 w-4 accent-green-600"
                    :disabled="!canUseBonuses"
                  >
                  <label for="use-bonus-in-check" class="text-xs md:text-sm text-gray-700 select-none">
                    Списать все накопленные бонусы
                  </label>
                </div>
                <Button
                  label="Оплата"
                  icon="pi pi-wallet"
                  severity="success"
                  size="small"
                  class="col-span-2"
                  @click="openPaymentDialog"
                />
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="showProductDecisionDialog"
    modal
    header="Добавление в чек"
    :style="{ width: '420px' }"
  >
    <p class="text-sm">Добавить в состав букета/корзины?</p>
    <template #footer>
      <Button label="Нет" severity="secondary" outlined @click="addSelectedProductDirectly" />
      <Button label="Да" severity="success" @click="openCompositionDialog" />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showCompositionDialog"
    modal
    header="Состав букета/корзины"
    :style="{ width: '960px', maxWidth: '96vw' }"
  >
    <div class="space-y-3">
      <div class="pt-2 space-y-2">
        <FloatLabel variant="on">
          <InputText id="composition-name" v-model.trim="compositionForm.name" class="w-full text-sm" />
          <label for="composition-name">Название</label>
        </FloatLabel>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
          <div class="grid grid-cols-1 gap-2">
          <FloatLabel variant="on">
            <Select
              id="composition-product-type"
              v-model="compositionForm.product_type_id"
              :options="productTypes"
              optionLabel="name"
              optionValue="id"
              class="w-full text-sm"
              required
            />
            <label for="composition-product-type">Тип продукта</label>
          </FloatLabel>

          <FloatLabel variant="on">
            <Select
              id="composition-product-level"
              v-model="compositionForm.product_level_id"
              :options="productLevels"
              optionLabel="name"
              optionValue="id"
              class="w-full text-sm"
              required
            />
            <label for="composition-product-level">Уровень сложности</label>
          </FloatLabel>

          </div>

          <div class="rounded-lg border border-green-200 bg-green-50 p-2">
            <p class="text-[11px] text-green-700">Общая стоимость витрины</p>
            <p class="font-semibold text-base text-green-700">{{ compositionFinalTotal }} ₸</p>
            <p class="text-[11px] text-green-700">Товары: {{ compositionBaseTotal }} ₸ + Тариф: {{ compositionForm.order_collection_rate }} ₸</p>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div class="border rounded-lg p-2">
          <div class="font-semibold text-sm mb-2">Товары:</div>
          <IconField class="w-full mb-2">
            <InputIcon><i class="pi pi-search" /></InputIcon>
            <InputText v-model.trim="compositionSearch" class="w-full text-sm" placeholder="Поиск товара..." />
          </IconField>

          <div class="max-h-72 overflow-auto space-y-2 pr-1">
            <div
              v-for="product in composerProducts"
              :key="`composer-${product.id}`"
              class="rounded-lg border p-2 flex items-center justify-between gap-2"
            >
              <div class="min-w-0">
                <p class="text-sm font-medium truncate">{{ product.name }}</p>
                <p v-if="product.barcode" class="text-xs text-gray-500">Штрихкод: {{ product.barcode }}</p>
                <p class="text-xs text-gray-500">Остаток: {{ product.stock }} | Цена: {{ product.price }} ₸</p>
              </div>
              <Button
                icon="pi pi-plus"
                size="small"
                label="Добавить"
                severity="success"
                @click="addComponentToComposition(product)"
                :disabled="product.stock <= 0"
              />
            </div>
          </div>
        </div>

        <div class="border rounded-lg p-2">
          <div class="font-semibold text-sm mb-2">Позиции новой витрины</div>
          <div v-if="!compositionForm.items.length" class="text-sm text-gray-400 py-8 text-center">
            Добавьте товары из левого списка
          </div>

          <div v-else class="max-h-72 overflow-auto space-y-2 pr-1">
            <div
              v-for="item in compositionForm.items"
              :key="`comp-item-${item.id}`"
              class="rounded-lg border p-2"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p class="text-sm font-medium truncate">{{ item.name }}</p>
                  <p v-if="item.barcode" class="text-xs text-gray-500">Штрихкод: {{ item.barcode }}</p>
                  <p class="text-xs text-gray-500">Остаток: {{ item.stock }} | Цена: {{ item.price }} ₸</p>
                </div>
                <Button icon="pi pi-trash" text severity="danger" size="small" @click="removeComponentFromComposition(item.id)" />
              </div>
              <div class="mt-2 flex items-center justify-between">
                <InputNumber
                  v-model="item.quantity"
                  :min="1"
                  :max="item.stock || 1"
                  inputClass="w-16 text-center text-sm"
                  @update:modelValue="clampCompositionItem(item)"
                />
                <p class="text-sm font-semibold">{{ item.price * item.quantity }} ₸</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Отмена" severity="secondary" outlined @click="showCompositionDialog = false" />
      <Button label="Добавить в корзину" severity="success" icon="pi pi-check" @click="addCompositionToCart" />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showCartCompositionDialog"
    modal
    header="Редактирование состава в чеке"
    :style="{ width: '1200px', maxWidth: '90vw' }"
  >
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div class="border rounded-lg p-2">
        <div class="font-semibold text-sm mb-2">Добавить товар</div>
        <IconField class="w-full mb-2">
          <InputIcon><i class="pi pi-search" /></InputIcon>
          <InputText v-model.trim="cartCompositionSearch" class="w-full text-sm" placeholder="Поиск товара..." />
        </IconField>

        <div class="max-h-80 overflow-auto space-y-2 pr-1">
          <div
            v-for="product in cartComposerProducts"
            :key="`cart-composer-${product.id}`"
            class="rounded-lg border p-2 flex items-center justify-between gap-2"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium truncate">{{ product.name }}</p>
              <p v-if="product.barcode" class="text-xs text-gray-500">Штрихкод: {{ product.barcode }}</p>
              <p class="text-xs text-gray-500">Остаток: {{ product.stock }} | Цена: {{ product.price }} ₸</p>
            </div>
            <Button
              icon="pi pi-plus"
              size="small"
              label="Добавить"
              severity="success"
              :disabled="product.stock <= 0"
              @click="addProductToCartComposition(product)"
            />
          </div>
        </div>
      </div>

      <div class="border rounded-lg p-2">
        <div class="font-semibold text-sm mb-2">Компоненты позиции</div>
        <div v-if="!cartCompositionDraft.items.length" class="text-sm text-gray-400 py-8 text-center">
          Компоненты отсутствуют
        </div>

        <div v-else class="max-h-72 overflow-auto space-y-2 pr-1">
          <div
            v-for="component in cartCompositionDraft.items"
            :key="`cart-component-${component.id}`"
            class="rounded-lg border p-2"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <p class="text-sm font-medium truncate">{{ component.name }}</p>
                <p v-if="component.barcode" class="text-xs text-gray-500">Штрихкод: {{ component.barcode }}</p>
                <p class="text-xs text-gray-500">Остаток: {{ component.stock }}</p>
              </div>
              <Button icon="pi pi-trash" text severity="danger" size="small" @click="removeProductFromCartComposition(component.id)" />
            </div>

            <div class="mt-2 grid grid-cols-2 gap-2">
              <div>
                <p class="text-[11px] text-gray-500 mb-1">Кол-во в составе</p>
                <InputNumber
                  v-model="component.quantity"
                  :min="1"
                  :max="component.stock || 1"
                  inputClass="w-full text-sm"
                  @update:modelValue="clampCartComponent(component)"
                />
              </div>
              <div>
                <p class="text-[11px] text-gray-500 mb-1">Цена компонента</p>
                <InputNumber
                  v-model="component.unitPrice"
                  :min="0"
                  inputClass="w-full text-sm"
                  @update:modelValue="clampCartComponentPrice(component)"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 border-t pt-2 space-y-2">
          <div class="flex items-center justify-between text-sm">
            <span>Сумма компонентов</span>
            <span class="font-semibold">{{ cartCompositionSubtotal }} ₸</span>
          </div>
          <div class="flex items-center justify-between text-sm">
            <span>Тариф сборки</span>
            <InputNumber v-model="cartCompositionDraft.order_collection_rate" :min="0" inputClass="w-24 text-right text-sm" />
          </div>
          <div class="flex items-center justify-between text-base font-semibold text-green-700">
            <span>Итог позиции</span>
            <span>{{ cartCompositionTotal }} ₸</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button label="Отмена" severity="secondary" outlined @click="showCartCompositionDialog = false" />
      <Button label="Сохранить" icon="pi pi-check" severity="success" @click="saveCartComposition" />
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showPaymentDialog"
    modal
    header="Оплата"
    :style="{ width: '520px', maxWidth: '96vw' }"
  >
    <div class="space-y-3">
      <div class="rounded-lg border border-gray-200 bg-gray-50 p-2">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600">Сумма чека</span>
          <span class="font-semibold">{{ totalAmount }} ₸</span>
        </div>
        <div class="flex items-center justify-between text-sm mt-1" :class="useClientBonuses ? 'text-green-700' : 'text-gray-500'">
          <span>Списание бонусов</span>
          <span>- {{ bonusDiscountAmount }} ₸</span>
        </div>
        <div class="flex items-center justify-between text-base font-semibold text-green-700 mt-2 pt-2 border-t border-green-100">
          <span>К оплате</span>
          <span>{{ payableAmount }} ₸</span>
        </div>
      </div>

      <div v-if="requiresAccountPayment">
        <label class="block mb-1 text-sm font-medium">Счет оплаты</label>
        <Select
          v-if="!isSplitPayment"
          v-model="selectedAccount"
          :options="availablePrimaryAccounts"
          optionLabel="label"
          filter
          :filterFields="['label', 'account', 'bank']"
          placeholder="Выберите счет"
          class="w-full"
        />
        <div v-else class="space-y-3">
          <div class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_140px]">
            <Select
              v-model="selectedAccount"
              :options="availablePrimaryAccounts"
              optionLabel="label"
              filter
              :filterFields="['label', 'account', 'bank']"
              placeholder="Первая часть оплаты"
              class="w-full"
            />
            <div>
              <InputNumber
                v-model="splitPaymentAmount"
                :min="0"
                :max="payableAmount"
                inputClass="w-full"
                class="w-full"
              />
            </div>
          </div>

          <div class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_140px]">
            <Select
              v-model="secondaryAccount"
              :options="availableSecondaryAccounts"
              optionLabel="label"
              filter
              :filterFields="['label', 'account', 'bank']"
              placeholder="Вторая часть оплаты"
              class="w-full"
            />
            <div>
              <InputNumber
                :modelValue="secondarySplitPaymentAmount"
                readonly
                inputClass="w-full"
                class="w-full"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
        Чек полностью закрывается бонусами. Выбор счета оплаты не требуется.
      </div>

      <div v-if="requiresAccountPayment" class="flex items-center gap-2">
        <input
          id="split-payment"
          v-model="isSplitPayment"
          type="checkbox"
          class="h-4 w-4 accent-green-600"
        >
        <label for="split-payment" class="text-sm text-gray-700 select-none">
          Смешанная оплата: часть наличными, часть картой
        </label>
      </div>
    </div>

    <template #footer>
      <Button label="Отмена" severity="secondary" outlined @click="showPaymentDialog = false" />
      <Button label="Провести оплату" icon="pi pi-check" severity="success" :loading="isSubmittingPayment" @click="submitPayment" />
    </template>
  </Dialog>

  <Dialog v-model:visible="addClientDialog" :style="{ width: '600px', maxWidth: '96vw' }" header="Добавить клиента" modal>
    <div class="grid grid-cols-12 justify-between">
      <div class="flex items-center col-span-12 gap-2 w-full">
        <FloatLabel variant="on" class="col-span-6 mt-2 w-1/2">
          <label for="sales-client-name" class="block z-10">ФИО</label>
          <InputText id="sales-client-name" v-model.trim="newClient.name" required fluid class="w-full" />
        </FloatLabel>
        <FloatLabel variant="on" class="col-span-6 mt-2 w-1/2">
          <label for="sales-client-phone" class="block z-10">Телефон</label>
          <InputMask
            id="sales-client-phone"
            v-model.trim="newClient.phone"
            required
            fluid
            mask="+7(799)999-99-99"
            placeholder="+7(799)999-99-99"
            class="w-full"
          />
        </FloatLabel>
      </div>

      <div class="flex items-center col-span-12 gap-2 mb-2">
        <FloatLabel variant="on" class="col-span-6 mt-2">
          <label for="sales-client-email" class="block z-10">Email</label>
          <InputText id="sales-client-email" v-model.trim="newClient.email" fluid />
        </FloatLabel>
        <FloatLabel variant="on" class="col-span-6 mt-2">
          <label for="sales-client-sex" class="block z-10">Пол</label>
          <Select v-model="newClient.sex" showClear :options="sexOptions" filter optionLabel="name" fluid required />
        </FloatLabel>
      </div>

      <div class="col-span-12">
        <FloatLabel variant="on" class="mt-2 block">
          <label for="sales-client-type" class="block z-10">Тип клиента</label>
          <Select
            id="sales-client-type"
            v-model="newClient.client_type_id"
            :options="clientTypeOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="Выберите тип клиента"
            class="w-full"
            fluid
          />
        </FloatLabel>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-evenly w-full gap-2">
        <Button label="Отменить" icon="pi pi-times" @click="closeAddClientDialog" severity="secondary" fluid />
        <Button label="Добавить" icon="pi pi-check" @click="createClientFromSales" severity="success" fluid />
      </div>
    </template>
  </Dialog>

  <Dialog
    v-model:visible="showShowcaseDetailsDialog"
    modal
    header="Детали витрины"
    :style="{ width: '760px', maxWidth: '96vw' }"
  >
    <div v-if="selectedShowcaseDetails" class="space-y-3">
      <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-base font-semibold">{{ selectedShowcaseDetails.name }}</p>
            <p class="text-xs text-gray-500 mt-1">Позиции: {{ selectedShowcaseDetails.components?.length || 0 }}</p>
          </div>
          <div class="text-right">
            <p class="text-xs text-gray-500">Тариф</p>
            <p class="font-semibold">{{ selectedShowcaseDetails.order_collection_rate || 0 }} ₸</p>
          </div>
        </div>
      </div>

      <div class="max-h-72 overflow-auto space-y-2 pr-1">
        <div
          v-for="component in selectedShowcaseDetails.components || []"
          :key="`showcase-detail-${selectedShowcaseDetails.id}-${component.store_id}-${component.product_id}`"
          class="rounded-lg border border-gray-200 bg-white p-2"
        >
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm font-medium">{{ component.name }}</p>
            <p class="text-sm font-semibold">{{ Number(component.unitPrice || 0) * Number(component.quantity || 0) }} ₸</p>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            Кол-во: {{ component.quantity }} | Цена: {{ component.unitPrice }} ₸ | Остаток: {{ component.showcaseQuantity }}
          </p>
        </div>
      </div>

      <div class="rounded-lg border border-green-200 bg-green-50 p-3">
        <div class="flex items-center justify-between text-sm">
          <span>Сумма компонентов</span>
          <span class="font-semibold">{{ selectedShowcaseBaseTotal }} ₸</span>
        </div>
        <div class="flex items-center justify-between text-sm mt-1">
          <span>Тариф сборки</span>
          <span class="font-semibold">{{ selectedShowcaseDetails.order_collection_rate || 0 }} ₸</span>
        </div>
        <div class="flex items-center justify-between text-base font-semibold text-green-700 mt-2 pt-2 border-t border-green-100">
          <span>Итог витрины</span>
          <span>{{ selectedShowcaseDetails.price || 0 }} ₸</span>
        </div>
      </div>
    </div>
  </Dialog>

  <!-- <Drawer v-model:visible="showHeldDrawer" position="right" header="Отложенные чеки" :style="{ width: '420px', maxWidth: '96vw' }">
    <div v-if="!heldCarts.length" class="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
      Отложенных чеков пока нет
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="held in heldCarts"
        :key="held.id"
        class="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <p class="truncate font-semibold text-slate-900">{{ held.title }}</p>
            <p class="mt-1 text-xs text-slate-500">{{ held.itemsCount }} поз. · {{ held.totalAmount }} ₸</p>
            <p class="text-xs text-slate-400">{{ held.createdAt }}</p>
          </div>
          <Button icon="pi pi-trash" text severity="danger" size="small" @click="removeHeldCart(held.id)" />
        </div>
        <div class="mt-3 flex gap-2">
          <Button label="Восстановить" icon="pi pi-replay" size="small" severity="success" @click="restoreHeldCart(held.id)" />
        </div>
      </div>
    </div>
  </Drawer> -->
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from '../lib/axios'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()
const FREQUENT_ITEMS_STORAGE_KEY = 'sales-frequent-items'
const HELD_CARTS_STORAGE_KEY = 'sales-held-carts'

const loading = ref(false)
const activeTab = ref('products')
const catalogView = ref('grid')
const searchQuery = ref('')
const selectedProductCategory = ref('Все')
const selectedProductSubcategory = ref('Все')
const selectedShowcaseProductTypeId = ref(null)
const selectedClient = ref(null)
const clientFilterValue = ref('')
const addClientDialog = ref(false)
const selectedAccount = ref(null)
const secondaryAccount = ref(null)
const isSplitPayment = ref(false)
const splitPaymentAmount = ref(0)

const products = ref([])
const showcases = ref([])
const clients = ref([])
const sexOptions = ref([])
const clientTypeOptions = ref([])
const accounts = ref([])
const extraWorkReferenceOptions = ref([])
const cartItems = ref([])
const isManualTotal = ref(false)
const manualTotal = ref(0)
const useClientBonuses = ref(false)
const showPaymentDialog = ref(false)
const isSubmittingPayment = ref(false)
const showProductDecisionDialog = ref(false)
const showCompositionDialog = ref(false)
const selectedProductForDecision = ref(null)
const compositionSearch = ref('')
const getTodayDateLabel = () => new Date().toISOString().slice(0, 10)
const compositionRateDateLabel = ref(getTodayDateLabel())
const productTypes = ref([])
const productLevels = ref([])
const employee = ref(null)
const showCartCompositionDialog = ref(false)
const showShowcaseDetailsDialog = ref(false)
const selectedShowcaseDetails = ref(null)
const cartCompositionSearch = ref('')
const cartCompositionEditingKey = ref(null)
const frequentItemKeys = ref([])
const heldCarts = ref([])
const SINGLE_PRODUCT_ORDER_TYPE_ID = 8
const EXTRA_WORK_ORDER_TYPE_ID = 9
const AUTO_EXTRA_WORK_KEY = 'extra-work-auto-adjustment'
const newClient = ref({
  name: '',
  phone: null,
  email: '',
  client_type_id: null,
  sex: null,
  sex_id: null,
})

const normalizeClientPhoneInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '')
  if (!digits) return null

  if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
    return `+7(${digits.slice(1, 4)})${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`
  }

  if (digits.length === 10) {
    return `+7(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`
  }

  return String(value || '').trim() || null
}

const productCategoryChips = computed(() => {
  const uniqueClassifications = [...new Set(
    (products.value || [])
      .map((item) => String(item.classification || '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'ru'))

  return ['Все', ...uniqueClassifications]
})

const productSubcategoryChips = computed(() => {
  const scopedProducts = (products.value || [])
    .filter((item) => Number(item.amount || 0) > 0)
    .filter((item) => {
      if (selectedProductCategory.value === 'Все') return true
      return String(item.classification || '').trim() === selectedProductCategory.value
    })

  const uniqueCategories = [...new Set(
    scopedProducts
      .map((item) => String(item.category || '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'ru'))

  return ['Все', ...uniqueCategories]
})

const showcaseTypeChips = computed(() => {
  const uniqueTypes = [...new Map(
    (showcases.value || [])
      .map((item) => {
        const label = String(item?.product_type_name || '').trim()
        const typeId = Number(item?.product_type_id || 0)

        if (!typeId || !label) return null

        return [typeId, { id: typeId, label }]
      })
      .filter(Boolean)
  ).values()].sort((a, b) => a.label.localeCompare(b.label, 'ru'))

  return [{ id: null, label: 'Все' }, ...uniqueTypes]
})
const showHeldDrawer = ref(false)
const editingPriceKey = ref(null)
const priceDraftValue = ref('')
const editingTotal = ref(false)
const totalDraftValue = ref('')
const compositionForm = ref({
  name: '',
  product_type_id: null,
  product_level_id: null,
  order_type_id: null,
  order_collection_rate: 0,
  date: new Date(),
  items: []
})
const cartCompositionDraft = ref({
  items: [],
  order_collection_rate: 0
})

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const getCartItemDisplayName = (item) => {
  if (item?.type === 'extra-work') {
    return String(item.extra_work_reference_name || item.name || 'Доп. работа').trim()
  }

  return String(item?.name || '').trim()
}

const extractBarcodeValue = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number') return String(value)

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (parsed && typeof parsed === 'object' && parsed.barcode !== undefined) {
        return String(parsed.barcode)
      }
      return value
    } catch {
      return value
    }
  }

  if (typeof value === 'object') {
    if (value.barcode !== undefined && value.barcode !== null) {
      return String(value.barcode)
    }
    return null
  }

  return null
}

const normalizeBarcodes = (rawBarcode) => {
  const source = Array.isArray(rawBarcode)
    ? rawBarcode
    : rawBarcode && typeof rawBarcode === 'object' && Array.isArray(rawBarcode.data)
      ? rawBarcode.data
      : [rawBarcode]

  const parsed = source
    .map((item) => extractBarcodeValue(item))
    .filter((item) => item && item.trim() !== '')

  return [...new Set(parsed)]
}

const formatExpiryDate = (value) => {
  const normalized = String(value || '').trim()
  if (!normalized) return ''

  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const [year, month, day] = normalized.split('-')
    return `${day}.${month}.${year}`
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) return normalized

  return parsed.toLocaleDateString('ru-RU')
}

const displayBarcodes = (rawBarcode, limit = 4) => {
  return normalizeBarcodes(rawBarcode).slice(0, limit)
}

const hiddenBarcodesCount = (rawBarcode, limit = 4) => {
  const total = normalizeBarcodes(rawBarcode).length
  return total > limit ? total - limit : 0
}

const getClientBonus = (client) => {
  if (!client) return 0
  return toNumber(
    client.bonus
    ?? client.bonuses
    ?? client.bonus_amount
    ?? client.bonuses_accumulated
    ?? client.accumulated_bonus
    ?? client.total_bonus
  )
}

const clientsWithGuest = computed(() => {
  const options = [{ id: null, label: '— Без клиента —', phone: '', bonus: 0 }]
  return options.concat((clients.value || []).map((client) => {
    const phone = String(client.phone || '').trim()
    const name = String(client.name || '').trim() || `Клиент #${client.id}`
    return {
      id: client.id,
      label: phone ? `${phone} · ${name}` : name,
      phone,
      bonus: getClientBonus(client),
      raw: client
    }
  }))
})

const showAddClientFromFilter = computed(() => clientFilterValue.value.trim().length > 0)

const extractErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data
  const firstValidationError = responseData?.errors
    ? Object.values(responseData.errors).flat()[0]
    : null

  return firstValidationError || responseData?.message || fallbackMessage
}

const extractSalePaymentErrorMessage = (error) => {
  const responseData = error?.response?.data
  const firstValidationError = responseData?.errors
    ? Object.values(responseData.errors).flat()[0]
    : null

  return firstValidationError || responseData?.message || 'Не удалось провести оплату'
}

const resetNewClientForm = () => {
  newClient.value = {
    name: '',
    phone: null,
    email: '',
    client_type_id: getDefaultClientTypeId(),
    sex: null,
    sex_id: null,
  }
}

const getDefaultClientTypeId = () => {
  return clientTypeOptions.value.find((item) => Number(item.id) === 1)?.id
    ?? clientTypeOptions.value[0]?.id
    ?? null
}

const openAddClientDialog = () => {
  const query = clientFilterValue.value.trim()

  if (query) {
    const hasDigits = /\d/.test(query)
    newClient.value.name = hasDigits ? '' : query
    newClient.value.phone = hasDigits ? normalizeClientPhoneInput(query) : null
    newClient.value.email = ''
    newClient.value.client_type_id = getDefaultClientTypeId()
    newClient.value.sex = null
    newClient.value.sex_id = null
  }

  addClientDialog.value = true
}

const closeAddClientDialog = () => {
  addClientDialog.value = false
  resetNewClientForm()
}

const handleClientFilter = (event) => {
  clientFilterValue.value = String(event?.value || '')
}

const resetClientFilterState = () => {
  clientFilterValue.value = ''
}

const fetchSexOptions = async () => {
  const res = await axios.get('/api/sex')
  sexOptions.value = Array.isArray(res.data) ? res.data : []
}

const fetchClients = async () => {
  const res = await axios.get('/api/client')
  clients.value = Array.isArray(res.data) ? res.data : []
}

const createClientFromSales = async () => {
  try {
    newClient.value.sex_id = newClient.value.sex?.id || null
    newClient.value.client_type_id = newClient.value.client_type_id || getDefaultClientTypeId()
    const response = await axios.post('/api/client', newClient.value)

    if (response.status === 201) {
      await fetchClients()

      const createdClientId = response.data?.id ?? null
      if (createdClientId) {
        selectedClient.value = clientsWithGuest.value.find((item) => item.id === createdClientId) || null
      }

      toast.add({ severity: 'success', summary: 'Успешно', detail: 'Клиент добавлен', life: 3000 })
      closeAddClientDialog()
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: extractErrorMessage(error, 'Не удалось добавить клиента'),
      life: 4000
    })
  }
}

const availablePrimaryAccounts = computed(() => {
  const blockedId = secondaryAccount.value?.id ?? null
  return (accounts.value || []).filter((account) => account.id !== blockedId)
})

const availableSecondaryAccounts = computed(() => {
  const blockedId = selectedAccount.value?.id ?? null
  return (accounts.value || []).filter((account) => account.id !== blockedId)
})

const selectedClientBonus = computed(() => {
  if (!selectedClient.value || !selectedClient.value.id) return 0
  return toNumber(selectedClient.value.bonus)
})

const canUseBonuses = computed(() => {
  return Boolean(selectedClient.value?.id) && selectedClientBonus.value > 0
})

const bonusDiscountAmount = computed(() => {
  if (!useClientBonuses.value || !canUseBonuses.value) return 0
  return Math.min(Number(totalAmount.value || 0), Math.max(0, selectedClientBonus.value))
})

const payableAmount = computed(() => {
  return Math.max(0, Number(totalAmount.value || 0) - bonusDiscountAmount.value)
})

const accruedBonusesAmount = computed(() => {
  return Math.round(Number(payableAmount.value || 0) * 0.02)
})

const requiresAccountPayment = computed(() => Number(payableAmount.value || 0) > 0)

const secondarySplitPaymentAmount = computed(() => {
  const payable = Number(payableAmount.value || 0)
  const firstPart = Math.max(0, Math.min(payable, Number(splitPaymentAmount.value || 0)))
  return Math.max(0, payable - firstPart)
})

const productItems = computed(() => {
  return (products.value || [])
    .filter((item) => Number(item.amount || 0) > 0)
    .filter((item) => {
      if (selectedProductCategory.value === 'Все') return true
      return String(item.classification || '').trim() === selectedProductCategory.value
    })
    .filter((item) => {
      if (selectedProductSubcategory.value === 'Все') return true
      return String(item.category || '').trim() === selectedProductSubcategory.value
    })
    .map((item) => {
      const normalizedBarcodes = normalizeBarcodes(item.barcode)
      const barcode = normalizedBarcodes[0] || ''

      return {
      id: item.id,
      store_id: item.id,
      product_id: item.product_id ?? null,
      key: `product-${item.id}`,
      type: 'product',
      name: item.name,
      image: item.image,
      classification: item.classification || '',
      category: item.category || '',
      barcode,
      barcodes: barcode ? [barcode] : [],
      expiryDate: item.date || '',
      expiryDateLabel: formatExpiryDate(item.date),
      price: Number(item.price || 0),
      stock: Number(item.amount || 0),
      maxQty: Number(item.amount || 0),
      }
    })
})

const showcaseItems = computed(() => {
  return (showcases.value || []).map((showcase) => {
    const items = showcase.items || []
    const itemsPrice = items.reduce((sum, item) => sum + (Number(item.price || 0) * Number(item.quantity || 0)), 0)
    const computedPrice = itemsPrice + Number(showcase.order_collection_rate || 0)
    const showcaseQty = Math.max(0, Number(showcase.quantity || 0))
    const showcaseBarcodes = normalizeBarcodes(showcase.barcode)

    return {
      id: showcase.id,
      key: `showcase-${showcase.id}`,
      type: 'showcase',
      name: showcase.name,
      image: showcase.image,
      barcode: showcaseBarcodes[0] || '',
      barcodes: showcaseBarcodes,
      order_type_id: showcase.order_type_id ?? null,
      product_type_id: showcase.product_type_id ?? null,
      product_type_name: showcase.product_type_name || '',
      order_collection_rate: Number(showcase.order_collection_rate || 0),
      components: items.map((item) => ({
        id: item.store_id,
        store_id: item.store_id,
        product_id: item.product_id ?? null,
        name: item.name,
        stock: Number(item.amount || 0),
        showcaseQuantity: Number(item.quantity || 0),
        quantity: Number(item.quantity || 1),
        unitPrice: Number(item.price || 0),
        image: item.image || null
      })),
      price: computedPrice,
      stock: showcaseQty,
      maxQty: showcaseQty,
    }
  }).filter((showcase) => {
    if (selectedShowcaseProductTypeId.value === null) return true
    return Number(showcase.product_type_id || 0) === Number(selectedShowcaseProductTypeId.value)
  })
})

const allCatalogItems = computed(() => [...productItems.value, ...showcaseItems.value])

const frequentItems = computed(() => {
  const keySet = new Set(frequentItemKeys.value)
  return allCatalogItems.value.filter((item) => keySet.has(item.key))
})

const heldCartItems = computed(() => {
  return heldCarts.value.map((held) => ({
    id: held.id,
    key: `held-${held.id}`,
    type: 'held',
    name: held.title,
    barcode: '',
    image: null,
    stock: held.itemsCount,
    price: held.totalAmount,
    held: true,
    heldId: held.id,
    createdAt: held.createdAt
  }))
})

const visibleItems = computed(() => {
  const list =
    activeTab.value === 'products'
      ? productItems.value
      : activeTab.value === 'showcases'
        ? showcaseItems.value
        : activeTab.value === 'frequent'
          ? frequentItems.value
          : heldCartItems.value
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return list
  return list.filter((item) => {
    const nameMatch = (item.name || '').toLowerCase().includes(q)
    const barcodeMatch = (item.barcode || '').toLowerCase().includes(q)
    return nameMatch || barcodeMatch
  })
})

const saveFrequentItems = () => {
  localStorage.setItem(FREQUENT_ITEMS_STORAGE_KEY, JSON.stringify(frequentItemKeys.value))
}

const loadFrequentItems = () => {
  try {
    const raw = localStorage.getItem(FREQUENT_ITEMS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    frequentItemKeys.value = Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : []
  } catch {
    frequentItemKeys.value = []
  }
}

const isFrequent = (itemKey) => frequentItemKeys.value.includes(itemKey)

const saveHeldCarts = () => {
  localStorage.setItem(HELD_CARTS_STORAGE_KEY, JSON.stringify(heldCarts.value))
}

const loadHeldCarts = () => {
  try {
    const raw = localStorage.getItem(HELD_CARTS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    heldCarts.value = Array.isArray(parsed) ? parsed : []
  } catch {
    heldCarts.value = []
  }
}

const toggleFrequent = (item) => {
  const itemKey = item?.key
  if (!itemKey) return

  if (isFrequent(itemKey)) {
    frequentItemKeys.value = frequentItemKeys.value.filter((key) => key !== itemKey)
    toast.add({ severity: 'info', summary: 'Убрано', detail: `${item.name} удален из Частых`, life: 2000 })
  } else {
    frequentItemKeys.value = [...frequentItemKeys.value, itemKey]
    toast.add({ severity: 'success', summary: 'Добавлено', detail: `${item.name} добавлен в Частые`, life: 2000 })
  }

  saveFrequentItems()
}

const selectedShowcaseBaseTotal = computed(() => {
  if (!selectedShowcaseDetails.value) return 0
  return (selectedShowcaseDetails.value.components || []).reduce((sum, component) => {
    return sum + (Number(component.unitPrice || 0) * Number(component.quantity || 0))
  }, 0)
})

const composerProducts = computed(() => {
  const q = compositionSearch.value.trim().toLowerCase()
  const list = productItems.value
  if (!q) return list
  return list.filter((item) => {
    const nameMatch = (item.name || '').toLowerCase().includes(q)
    const barcodeMatch = (item.barcode || '').toLowerCase().includes(q)
    return nameMatch || barcodeMatch
  })
})

const compositionBaseTotal = computed(() => {
  return compositionForm.value.items.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0)
})

const compositionFinalTotal = computed(() => {
  return compositionBaseTotal.value + Number(compositionForm.value.order_collection_rate || 0)
})

const cartComposerProducts = computed(() => {
  const q = cartCompositionSearch.value.trim().toLowerCase()
  const list = productItems.value
  if (!q) return list
  return list.filter((item) => {
    const nameMatch = (item.name || '').toLowerCase().includes(q)
    const barcodeMatch = (item.barcode || '').toLowerCase().includes(q)
    return nameMatch || barcodeMatch
  })
})

const cartCompositionSubtotal = computed(() => {
  return cartCompositionDraft.value.items.reduce((sum, item) => {
    return sum + Number(item.unitPrice || 0) * Number(item.quantity || 0)
  }, 0)
})

const cartCompositionTotal = computed(() => {
  return cartCompositionSubtotal.value + Number(cartCompositionDraft.value.order_collection_rate || 0)
})

const cartSubtotal = computed(() => {
  return cartItems.value.reduce((sum, item) => sum + getRoundedLineTotal(item), 0)
})

const cartBaseSubtotal = computed(() => {
  return cartItems.value.reduce((sum, item) => {
    if (item.autoAdjustment) {
      return sum
    }

    return sum + getRoundedLineTotal(item)
  }, 0)
})

const totalAmount = computed(() => {
  return isManualTotal.value ? Number(manualTotal.value || 0) : cartSubtotal.value
})

const isCustomTotal = computed(() => {
  return Number(totalAmount.value || 0) !== Number(cartBaseSubtotal.value || 0)
})

const totalDelta = computed(() => {
  return Number(totalAmount.value || 0) - Number(cartBaseSubtotal.value || 0)
})

const totalDeltaLabel = computed(() => {
  const delta = Number(totalDelta.value || 0)
  if (delta === 0) return ''
  const sign = delta > 0 ? '+' : '−'
  return `${sign}${Math.abs(delta)} ₸`
})

watch(cartSubtotal, (value) => {
  if (!isManualTotal.value) {
    manualTotal.value = value
  }
}, { immediate: true })

watch(productCategoryChips, (chips) => {
  if (!chips.includes(selectedProductCategory.value)) {
    selectedProductCategory.value = 'Все'
  }
}, { immediate: true })

watch(selectedProductCategory, () => {
  selectedProductSubcategory.value = 'Все'
})

watch(productSubcategoryChips, (chips) => {
  if (!chips.includes(selectedProductSubcategory.value)) {
    selectedProductSubcategory.value = 'Все'
  }
}, { immediate: true })

watch(showcaseTypeChips, (chips) => {
  if (!chips.some((chip) => chip.id === selectedShowcaseProductTypeId.value)) {
    selectedShowcaseProductTypeId.value = null
  }
}, { immediate: true })

watch(canUseBonuses, (value) => {
  if (!value) {
    useClientBonuses.value = false
  }
})

watch(selectedAccount, (value) => {
  if (secondaryAccount.value?.id && secondaryAccount.value.id === value?.id) {
    secondaryAccount.value = null
  }
})

watch(secondaryAccount, (value) => {
  if (selectedAccount.value?.id && selectedAccount.value.id === value?.id) {
    selectedAccount.value = null
  }
})

const addToCart = (item) => {
  if (Number(item.maxQty || 0) <= 0) {
    const detail = item.type === 'showcase'
      ? 'По одной или нескольким позициям витрины закончился остаток'
      : 'Товар закончился на складе'
    toast.add({ severity: 'warn', summary: 'Недостаточно остатков', detail, life: 2500 })
    return
  }

  const existing = cartItems.value.find((cart) => cart.key === item.key)
  if (existing) {
    if (existing.quantity < existing.maxQty) {
      existing.quantity += 1
    }
    return
  }

  cartItems.value.push({
    ...item,
    quantity: 1,
    basePrice: Number(item.price || 0),
    unitPrice: Number(item.price || 0),
    components: item.components ? [...item.components] : [],
    order_collection_rate: Number(item.order_collection_rate || 0),
  })
}

const addExtraWorkLine = () => {
  if (!extraWorkReferenceOptions.value.length) {
    toast.add({
      severity: 'warn',
      summary: 'Справочник пуст',
      detail: 'Сначала добавьте значения в справочнике "Доп. работа".',
      life: 3000
    })
    return
  }

  const uniqueKey = `extra-work-${Date.now()}-${Math.floor(Math.random() * 10000)}`
  cartItems.value.push({
    id: uniqueKey,
    key: uniqueKey,
    type: 'extra-work',
    name: 'Доп. работа',
    extra_work_reference_id: null,
    extra_work_reference_name: '',
    image: null,
    barcode: '',
    barcodes: [],
    quantity: 1,
    stock: 1,
    maxQty: 1,
    price: 0,
    basePrice: 0,
    unitPrice: 0,
    components: [],
    order_collection_rate: 0,
    product_type_id: null,
    product_level_id: null,
    order_type_id: EXTRA_WORK_ORDER_TYPE_ID,
    autoAdjustment: false,
  })
}

const setExtraWorkReference = (item, referenceId) => {
  const selectedReference = extraWorkReferenceOptions.value.find((option) => Number(option.id) === Number(referenceId))
  item.extra_work_reference_id = selectedReference?.id ?? null
  item.extra_work_reference_name = selectedReference?.name ?? ''
}

const upsertAutoExtraWorkLine = (amount) => {
  const nextAmount = Math.max(0, Math.round(Number(amount || 0)))
  const existing = cartItems.value.find((item) => item.key === AUTO_EXTRA_WORK_KEY)

  if (nextAmount <= 0) {
    if (existing) {
      cartItems.value = cartItems.value.filter((item) => item.key !== AUTO_EXTRA_WORK_KEY)
    }
    return
  }

  if (existing) {
    existing.price = nextAmount
    existing.basePrice = nextAmount
    existing.unitPrice = nextAmount
    existing.quantity = 1
    existing.stock = 1
    existing.maxQty = 1
    return
  }

  cartItems.value.push({
    id: AUTO_EXTRA_WORK_KEY,
    key: AUTO_EXTRA_WORK_KEY,
    type: 'extra-work',
    name: 'Доп. работа',
    extra_work_reference_id: null,
    extra_work_reference_name: '',
    image: null,
    barcode: '',
    barcodes: [],
    quantity: 1,
    stock: 1,
    maxQty: 1,
    price: nextAmount,
    basePrice: nextAmount,
    unitPrice: nextAmount,
    components: [],
    order_collection_rate: 0,
    product_type_id: null,
    product_level_id: null,
    order_type_id: EXTRA_WORK_ORDER_TYPE_ID,
    autoAdjustment: true,
  })
}

const onCatalogItemClick = (item) => {
  if (item.type === 'held' && item.heldId) {
    restoreHeldCart(item.heldId)
    return
  }
  addToCart(item)
  searchQuery.value = ''
}

const openShowcaseDetails = (showcase) => {
  selectedShowcaseDetails.value = showcase
  showShowcaseDetailsDialog.value = true
}

const addSelectedProductDirectly = () => {
  if (selectedProductForDecision.value) {
    addToCart(selectedProductForDecision.value)
  }
  showProductDecisionDialog.value = false
  selectedProductForDecision.value = null
}

const openCompositionDialog = () => {
  if (!selectedProductForDecision.value) return

  compositionForm.value = {
    name: `Букет: ${selectedProductForDecision.value.name}`,
    product_type_id: null,
    product_level_id: null,
    order_type_id: null,
    order_collection_rate: 0,
    date: new Date(),
    items: []
  }
  compositionRateDateLabel.value = getTodayDateLabel()
  compositionSearch.value = ''
  addComponentToComposition(selectedProductForDecision.value)

  showProductDecisionDialog.value = false
  showCompositionDialog.value = true
}

const addComponentToComposition = (product) => {
  const existing = compositionForm.value.items.find((item) => item.id === product.id)
  if (existing) {
    if (existing.quantity < existing.stock) {
      existing.quantity += 1
    }
    return
  }

  compositionForm.value.items.push({
    id: product.id,
    store_id: product.store_id ?? product.id,
    product_id: product.product_id ?? null,
    key: product.key,
    name: product.name,
    barcode: product.barcode || '',
    price: Number(product.price || 0),
    stock: Number(product.stock || 0),
    quantity: 1,
    image: product.image
  })
}

const removeComponentFromComposition = (id) => {
  compositionForm.value.items = compositionForm.value.items.filter((item) => item.id !== id)
}

const clampCompositionItem = (item) => {
  const max = Math.max(1, Number(item.stock || 1))
  const current = Number(item.quantity || 1)
  item.quantity = Math.max(1, Math.min(current, max))
}

const addCompositionToCart = () => {
  if (!compositionForm.value.items.length) {
    toast.add({ severity: 'warn', summary: 'Пустой состав', detail: 'Добавьте товары в состав', life: 2500 })
    return
  }

  const maxQty = Math.min(
    ...compositionForm.value.items.map((item) => Math.floor(Number(item.stock || 0) / Math.max(1, Number(item.quantity || 1))))
  )

  if (!Number.isFinite(maxQty) || maxQty <= 0) {
    toast.add({ severity: 'warn', summary: 'Недостаточно остатков', detail: 'Невозможно собрать состав из выбранных позиций', life: 3000 })
    return
  }

  const uniqueKey = `custom-showcase-${Date.now()}-${Math.floor(Math.random() * 10000)}`
  const name = compositionForm.value.name?.trim() || 'Составной букет'
  const firstImage = compositionForm.value.items[0]?.image || null

  addToCart({
    id: uniqueKey,
    key: uniqueKey,
    type: 'showcase',
    name,
    image: firstImage,
    product_type_id: compositionForm.value.product_type_id ?? null,
    product_level_id: compositionForm.value.product_level_id ?? null,
    order_type_id: compositionForm.value.order_type_id ?? null,
    price: compositionFinalTotal.value,
    stock: maxQty,
    maxQty,
    components: compositionForm.value.items.map((item) => ({
      id: item.id,
      store_id: item.store_id ?? item.id,
      product_id: item.product_id ?? null,
      key: item.key,
      name: item.name,
      barcode: item.barcode || '',
      stock: Number(item.stock || 0),
      quantity: Number(item.quantity || 1),
      unitPrice: Number(item.price || 0),
      image: item.image || null
    })),
    order_collection_rate: Number(compositionForm.value.order_collection_rate || 0),
  })

  showCompositionDialog.value = false
  selectedProductForDecision.value = null
}

const openCartCompositionDialog = (cartItem) => {
  cartCompositionEditingKey.value = cartItem.key
  cartCompositionSearch.value = ''
  cartCompositionDraft.value = {
    order_collection_rate: Number(cartItem.order_collection_rate || 0),
    items: (cartItem.components || []).map((item) => ({
      id: item.id,
      store_id: item.store_id ?? item.id,
      product_id: item.product_id ?? null,
      key: item.key,
      name: item.name,
      barcode: item.barcode || '',
      stock: Number(item.stock || 0),
      quantity: Number(item.quantity || 1),
      unitPrice: Number(item.unitPrice || 0),
      image: item.image || null
    }))
  }
  showCartCompositionDialog.value = true
}

const addProductToCartComposition = (product) => {
  const exists = cartCompositionDraft.value.items.find((item) => item.id === product.id)
  if (exists) {
    if (exists.quantity < exists.stock) {
      exists.quantity += 1
    }
    return
  }

  cartCompositionDraft.value.items.push({
    id: product.id,
    store_id: product.store_id ?? product.id,
    product_id: product.product_id ?? null,
    key: product.key,
    name: product.name,
    barcode: product.barcode || '',
    stock: Number(product.stock || 0),
    quantity: 1,
    unitPrice: Number(product.price || 0),
    image: product.image || null
  })
}

const removeProductFromCartComposition = (id) => {
  cartCompositionDraft.value.items = cartCompositionDraft.value.items.filter((item) => item.id !== id)
}

const clampCartComponent = (item) => {
  const max = Math.max(1, Number(item.stock || 1))
  const current = Number(item.quantity || 1)
  item.quantity = Math.max(1, Math.min(current, max))
}

const clampCartComponentPrice = (item) => {
  const next = Number(item.unitPrice || 0)
  item.unitPrice = next < 0 ? 0 : next
}

const startPriceEdit = (item) => {
  editingPriceKey.value = item.key
  priceDraftValue.value = String(Number(item.unitPrice || 0))
}

const cancelPriceEdit = () => {
  editingPriceKey.value = null
  priceDraftValue.value = ''
}

const updateUnitPrice = (itemId, price) => {
  const item = cartItems.value.find((cartItem) => cartItem.key === itemId)
  if (!item) return

  const next = Number(price)
  item.unitPrice = Number.isFinite(next) && next >= 0 ? next : 0
}

const applyPriceEdit = (item) => {
  if (editingPriceKey.value !== item.key) {
    return
  }

  updateUnitPrice(item.key, priceDraftValue.value)
  cancelPriceEdit()
}

const saveCartComposition = () => {
  if (!cartCompositionEditingKey.value) return

  if (!cartCompositionDraft.value.items.length) {
    toast.add({ severity: 'warn', summary: 'Пустой состав', detail: 'Добавьте хотя бы один компонент', life: 2500 })
    return
  }

  const maxQty = Math.min(
    ...cartCompositionDraft.value.items.map((item) => Math.floor(Number(item.stock || 0) / Math.max(1, Number(item.quantity || 1))))
  )

  if (!Number.isFinite(maxQty) || maxQty <= 0) {
    toast.add({ severity: 'warn', summary: 'Недостаточно остатков', detail: 'Невозможно сохранить состав с текущими значениями', life: 3000 })
    return
  }

  const target = cartItems.value.find((item) => item.key === cartCompositionEditingKey.value)
  if (!target) return

  target.components = cartCompositionDraft.value.items.map((item) => ({
    id: item.id,
    store_id: item.store_id ?? item.id,
    product_id: item.product_id ?? null,
    key: item.key,
    name: item.name,
    barcode: item.barcode || '',
    stock: Number(item.stock || 0),
    quantity: Number(item.quantity || 1),
    unitPrice: Number(item.unitPrice || 0),
    image: item.image || null
  }))
  target.order_collection_rate = Number(cartCompositionDraft.value.order_collection_rate || 0)
  target.maxQty = maxQty
  if (target.quantity > maxQty) {
    target.quantity = maxQty
  }

  const nextUnitPrice = target.components.reduce((sum, item) => {
    return sum + Number(item.unitPrice || 0) * Number(item.quantity || 0)
  }, 0) + target.order_collection_rate

  target.basePrice = nextUnitPrice
  target.unitPrice = nextUnitPrice

  showCartCompositionDialog.value = false
  toast.add({ severity: 'success', summary: 'Сохранено', detail: 'Состав позиции обновлён', life: 2000 })
}

watch(
  () => [compositionForm.value.product_level_id, compositionForm.value.product_type_id],
  async ([levelId, typeId]) => {
    if (!showCompositionDialog.value) return

    if (!levelId || !typeId) {
      compositionForm.value.order_type_id = null
      compositionForm.value.order_collection_rate = 0
      compositionRateDateLabel.value = getTodayDateLabel()
      return
    }

    try {
      const res = await axios.get('/api/get-order-type-rate-info', {
        params: {
          product_level_id: levelId,
          product_type_id: typeId
        }
      })
      console.log(getTodayDateLabel())
      compositionForm.value.order_type_id = res.data?.order_type_id ?? null
      compositionForm.value.order_collection_rate = Number(res.data?.order_collection_rate || 0)
      compositionRateDateLabel.value = res.data?.date ?? getTodayDateLabel()
    } catch (error) {
      compositionForm.value.order_type_id = null
      compositionForm.value.order_collection_rate = 0
      compositionRateDateLabel.value = getTodayDateLabel()
    }
  }
)

const removeFromCart = (key) => {
  cartItems.value = cartItems.value.filter((item) => item.key !== key)
}

const clearCart = () => {
  cartItems.value = []
  isManualTotal.value = false
  manualTotal.value = 0
  useClientBonuses.value = false
}

const resetCartState = () => {
  cartItems.value = []
  selectedClient.value = null
  selectedAccount.value = null
  secondaryAccount.value = null
  isSplitPayment.value = false
  splitPaymentAmount.value = 0
  isManualTotal.value = false
  manualTotal.value = 0
  useClientBonuses.value = false
}

const holdCart = () => {
  if (!cartItems.value.length) {
    toast.add({ severity: 'warn', summary: 'Пустой чек', detail: 'Нечего откладывать', life: 2500 })
    return
  }

  const timestamp = Date.now()
  const dateLabel = new Date(timestamp).toLocaleString('ru-RU')
  const clientLabel = selectedClient.value?.label || 'Без клиента'

  heldCarts.value.unshift({
    id: timestamp,
    title: `${clientLabel}`,
    createdAt: dateLabel,
    itemsCount: cartItems.value.length,
    totalAmount: Number(totalAmount.value || 0),
    cartItems: JSON.parse(JSON.stringify(cartItems.value)),
    selectedClient: selectedClient.value,
    selectedAccount: selectedAccount.value,
    secondaryAccount: secondaryAccount.value,
    isSplitPayment: Boolean(isSplitPayment.value),
    splitPaymentAmount: Number(splitPaymentAmount.value || 0),
    manualTotal: Number(manualTotal.value || 0),
    isManualTotal: Boolean(isManualTotal.value),
    useClientBonuses: Boolean(useClientBonuses.value)
  })

  saveHeldCarts()
  resetCartState()
  toast.add({ severity: 'success', summary: 'Чек отложен', detail: 'Чек сохранен в отложенные', life: 2500 })
}

const removeHeldCart = (heldId) => {
  heldCarts.value = heldCarts.value.filter((item) => item.id !== heldId)
  saveHeldCarts()
}

const restoreHeldCart = (heldId) => {
  const held = heldCarts.value.find((item) => item.id === heldId)
  if (!held) return

  cartItems.value = JSON.parse(JSON.stringify(held.cartItems || []))
  selectedClient.value = held.selectedClient || null
  selectedAccount.value = held.selectedAccount || null
  secondaryAccount.value = held.secondaryAccount || null
  isSplitPayment.value = Boolean(held.isSplitPayment)
  splitPaymentAmount.value = Number(held.splitPaymentAmount || 0)
  manualTotal.value = Number(held.manualTotal || 0)
  isManualTotal.value = Boolean(held.isManualTotal)
  useClientBonuses.value = Boolean(held.useClientBonuses)

  heldCarts.value = heldCarts.value.filter((item) => item.id !== heldId)
  saveHeldCarts()
  showHeldDrawer.value = false
  toast.add({ severity: 'success', summary: 'Чек восстановлен', detail: 'Отложенный чек перенесен в активный', life: 2500 })
}

const clampQuantity = (item) => {
  const max = Math.max(1, Number(item.maxQty || 1))
  const current = Math.floor(Number(item.quantity || 1))
  item.quantity = Math.max(1, Math.min(Number.isFinite(current) ? current : 1, max))
}

const decrementCartItem = (item) => {
  item.quantity = Math.max(1, Number(item.quantity || 1) - 1)
  clampQuantity(item)
}

const incrementCartItem = (item) => {
  item.quantity = Number(item.quantity || 1) + 1
  clampQuantity(item)
}

const getRoundedLineTotal = (item) => {
  return Math.round(Number(item.unitPrice || 0) * Number(item.quantity || 0))
}

const clampUnitPrice = (item) => {
  const next = Number(item.unitPrice || 0)
  item.unitPrice = next < 0 ? 0 : next
}

const toggleManualTotal = () => {
  isManualTotal.value = !isManualTotal.value
  if (!isManualTotal.value) {
    manualTotal.value = cartSubtotal.value
  }
}

const startTotalEdit = () => {
  editingTotal.value = true
  totalDraftValue.value = String(Number(totalAmount.value || 0))
}

const cancelTotalEdit = () => {
  editingTotal.value = false
  totalDraftValue.value = ''
}

const applyTotalEdit = () => {
  if (!editingTotal.value) return

  const next = Math.round(Number(totalDraftValue.value))
  const baseTotal = Math.round(Number(cartBaseSubtotal.value || 0))

  if (!Number.isFinite(next)) {
    cancelTotalEdit()
    return
  }

  if (next < baseTotal) {
    toast.add({
      severity: 'warn',
      summary: 'Сумму нельзя уменьшить',
      detail: 'Итоговую сумму можно менять только в большую сторону. Разница будет добавлена строкой "Доп. работа".',
      life: 3500
    })
    cancelTotalEdit()
    return
  }

  upsertAutoExtraWorkLine(next - baseTotal)
  isManualTotal.value = false
  manualTotal.value = 0
  cancelTotalEdit()
}

const resetTotalPrice = () => {
  upsertAutoExtraWorkLine(0)
  isManualTotal.value = false
  manualTotal.value = cartBaseSubtotal.value
  cancelTotalEdit()
}

const resetLinePrice = (item) => {
  item.unitPrice = Number(item.basePrice || 0)
}

const resetPrice = (item) => {
  resetLinePrice(item)
  if (editingPriceKey.value === item.key) {
    cancelPriceEdit()
  }
}

const openPaymentDialog = () => {
  if (!cartItems.value.length) {
    toast.add({ severity: 'warn', summary: 'Пустой чек', detail: 'Добавьте товары перед оплатой', life: 2500 })
    return
  }
  if (!isSplitPayment.value) {
    splitPaymentAmount.value = Number(payableAmount.value || 0)
  }
  showPaymentDialog.value = true
}

watch(payableAmount, (value) => {
  const payable = Number(value || 0)
  if (payable <= 0) {
    isSplitPayment.value = false
    splitPaymentAmount.value = 0
    selectedAccount.value = null
    secondaryAccount.value = null
    return
  }

  if (!isSplitPayment.value) {
    splitPaymentAmount.value = payable
    return
  }

  if (Number(splitPaymentAmount.value || 0) > payable) {
    splitPaymentAmount.value = payable
  }
})

watch(isSplitPayment, (enabled) => {
  if (enabled) {
    splitPaymentAmount.value = Number(payableAmount.value || 0)
    return
  }

  secondaryAccount.value = null
  splitPaymentAmount.value = Number(payableAmount.value || 0)
})

const resolveDefaultTypeLevel = () => {
  const fallbackTypeId = productTypes.value[0]?.id ?? null
  const fallbackLevelId = productLevels.value[0]?.id ?? null

  return {
    product_type: fallbackTypeId ? { id: fallbackTypeId } : null,
    product_level: fallbackLevelId ? { id: fallbackLevelId } : null
  }
}

const buildSaleItemsPayload = () => {
  const defaults = resolveDefaultTypeLevel()

  return cartItems.value.map((item) => {
    const qty = Math.max(1, Number(item.quantity || 1))
    const sourceComponents = item.type === 'extra-work'
      ? []
      : item.components?.length
      ? item.components
      : [{
          id: item.id,
          store_id: item.store_id ?? item.id,
          product_id: item.product_id ?? null,
          quantity: 1,
          unitPrice: Number(item.unitPrice || 0)
        }]

    const components = sourceComponents.map((component) => ({
      product_id: component.product_id ?? null,
      store_id: component.store_id ?? component.id ?? null,
      quantity: Math.max(1, Number(component.quantity || 1)) * qty,
      unit_price: Math.max(0, Number(component.unitPrice || 0))
    }))

    const lineTotal = Math.max(0, getRoundedLineTotal(item))

    return {
      order_type_id: item.type === 'product'
        ? SINGLE_PRODUCT_ORDER_TYPE_ID
        : item.type === 'extra-work'
          ? EXTRA_WORK_ORDER_TYPE_ID
          : item.order_type_id ?? null,
      title: item.type === 'showcase' || item.type === 'extra-work'
        ? String(item.type === 'extra-work' ? getCartItemDisplayName(item) : item.name || '').trim() || null
        : null,
      product_type: item.product_type_id ? { id: item.product_type_id } : defaults.product_type,
      product_level: item.product_level_id ? { id: item.product_level_id } : defaults.product_level,
      order_collection_rate: Number(item.order_collection_rate || 0),
      quantity: 1,
      unit_price: lineTotal,
      total_price: lineTotal,
      components
    }
  })
}

const submitPayment = async () => {
  if (isSubmittingPayment.value) return

  if (requiresAccountPayment.value && !selectedAccount.value?.id) {
    toast.add({ severity: 'warn', summary: 'Счет не выбран', detail: 'Выберите счет для оплаты', life: 2500 })
    return
  }
  if (requiresAccountPayment.value && isSplitPayment.value) {
    if (!secondaryAccount.value?.id) {
      toast.add({ severity: 'warn', summary: 'Счет не выбран', detail: 'Выберите второй счет для смешанной оплаты', life: 2500 })
      return
    }
    if (selectedAccount.value.id === secondaryAccount.value.id) {
      toast.add({ severity: 'warn', summary: 'Одинаковые счета', detail: 'Для смешанной оплаты выберите разные счета', life: 2500 })
      return
    }
    if (Number(splitPaymentAmount.value || 0) <= 0 || Number(secondarySplitPaymentAmount.value || 0) <= 0) {
      toast.add({ severity: 'warn', summary: 'Неверная сумма', detail: 'Обе части оплаты должны быть больше нуля', life: 2500 })
      return
    }
  }
  if (!employee.value?.id) {
    toast.add({ severity: 'warn', summary: 'Сотрудник не найден', detail: 'Невозможно провести оплату без сотрудника', life: 3000 })
    return
  }
  if (!cartItems.value.length) {
    toast.add({ severity: 'warn', summary: 'Пустой чек', detail: 'Добавьте товары перед оплатой', life: 2500 })
    return
  }

  const hasUnselectedExtraWork = cartItems.value.some((item) => {
    return item.type === 'extra-work' && !item.autoAdjustment && !item.extra_work_reference_id
  })

  if (hasUnselectedExtraWork) {
    toast.add({
      severity: 'warn',
      summary: 'Выберите доп. работу',
      detail: 'Заполните вид доп. работы для всех добавленных строк.',
      life: 3000
    })
    return
  }

  const paymentParts = !requiresAccountPayment.value
    ? []
    : isSplitPayment.value
    ? [
        { account_id: selectedAccount.value.id, amount: Number(splitPaymentAmount.value || 0) },
        { account_id: secondaryAccount.value.id, amount: Number(secondarySplitPaymentAmount.value || 0) }
      ]
    : [
        { account_id: selectedAccount.value.id, amount: Number(payableAmount.value || 0) }
      ]

  const payload = {
    date: new Date().toISOString().slice(0, 19).replace('T', ' '),
    client: { id: selectedClient.value?.id ?? null },
    account: { id: requiresAccountPayment.value ? (selectedAccount.value?.id ?? null) : null },
    payment_parts: paymentParts,
    employee: { id: employee.value.id },
    total_amount: Number(totalAmount.value || 0),
    price_after_discount: Number(payableAmount.value || 0),
    bonuses: Number(accruedBonusesAmount.value || 0),
    isBonusUsed: Boolean(useClientBonuses.value && canUseBonuses.value),
    items: buildSaleItemsPayload()
  }

  if (!payload.items.length) {
    toast.add({ severity: 'warn', summary: 'Пустой чек', detail: 'Добавьте товары перед оплатой', life: 2500 })
    return
  }

  isSubmittingPayment.value = true
  try {
    await axios.post('/api/sale', payload)
    toast.add({ severity: 'success', summary: 'Оплата проведена', detail: 'Продажа успешно сохранена', life: 2500 })

    cartItems.value = []
    showPaymentDialog.value = false
    useClientBonuses.value = false
    selectedAccount.value = null
    secondaryAccount.value = null
    isSplitPayment.value = false
    splitPaymentAmount.value = 0
    isManualTotal.value = false

    await fetchData()
  } catch (error) {
    const message = extractSalePaymentErrorMessage(error)
    toast.add({ severity: 'error', summary: 'Ошибка оплаты', detail: message, life: 3500 })
  } finally {
    isSubmittingPayment.value = false
  }
}

const fetchData = async () => {
  loading.value = true
  try {
    const barcode = localStorage.getItem('barcode')
    const [storageOneRes, storageTwoRes, showcasesRes, clientsRes, levelsRes, typesRes, accountsRes, employeeRes, sexRes, clientTypeRes, extraWorkReferenceRes] = await Promise.all([
      axios.get('/api/store', { params: { storage: 1, price_source: 'daily_only' } }),
      axios.get('/api/store', { params: { storage: 2, price_source: 'daily_only' } }),
      axios.get('/api/showcases'),
      axios.get('/api/client'),
      axios.get('/api/get-product-levels'),
      axios.get('/api/get-product-types'),
      axios.get('/api/account').catch(() => ({ data: [] })),
      axios.get('/api/get-employee', { params: { barcode } }).catch(() => ({ data: null })),
      axios.get('/api/sex').catch(() => ({ data: [] })),
      axios.get('/api/client-types').catch(() => ({ data: [] })),
      axios.get('/api/extra-work-references').catch(() => ({ data: [] }))
    ])

    const storageOneProducts = Array.isArray(storageOneRes.data) ? storageOneRes.data : []
    const storageTwoProducts = Array.isArray(storageTwoRes.data) ? storageTwoRes.data : []

    products.value = [...storageOneProducts, ...storageTwoProducts]
    showcases.value = Array.isArray(showcasesRes.data) ? showcasesRes.data : []
    clients.value = Array.isArray(clientsRes.data) ? clientsRes.data : []
    sexOptions.value = Array.isArray(sexRes.data) ? sexRes.data : []
    clientTypeOptions.value = Array.isArray(clientTypeRes.data) ? clientTypeRes.data : []
    extraWorkReferenceOptions.value = Array.isArray(extraWorkReferenceRes.data) ? extraWorkReferenceRes.data : []
    productLevels.value = Array.isArray(levelsRes.data) ? levelsRes.data : []
    productTypes.value = Array.isArray(typesRes.data) ? typesRes.data : []
    accounts.value = (Array.isArray(accountsRes.data) ? accountsRes.data : []).map((account) => ({
      ...account,
      label: `${account.bank} · ${account.account} (${account.currency})`
    }))
    employee.value = {
      barcode: 818429381021,
      currency: "KZT",
      date: "2025-11-30",
      id: 1,
      name:"Amy Larson",
      payment_method: "за смену",
      position:"Florist",
      tariff: 15000
    }
    // employee.value = employeeRes.data || 1
    if (!newClient.value.client_type_id) {
      newClient.value.client_type_id = getDefaultClientTypeId()
    }
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadFrequentItems()
  loadHeldCarts()
  fetchData()
})
</script>

<style scoped>
.sales-page {
  --accent: rgb(16 185 129);
}

.cart-price-custom {
  color: var(--accent);
}

.favorite-star-active {
  color: rgb(249 115 22) !important;
}

.cart-stepper-action {
  display: inline-flex;
  height: 1.45rem;
  width: 1.45rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: rgb(22 37 33);
  font-size: 1.15rem;
  line-height: 1;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.cart-stepper-action:hover {
  background: rgb(245 245 244);
}

.cart-price-input :deep(.p-inputnumber-input) {
  border-radius: 1rem;
  border: 1px solid rgb(226 232 240);
  background: rgb(255 255 255);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
  padding-inline: 1rem;
  color: rgb(15 23 42);
}

@media (max-width: 1180px) {
  .sales-page :deep(.p-inputtext),
  .sales-page :deep(.p-select-label),
  .sales-page :deep(.p-select-option),
  .sales-page :deep(.p-button-label) {
    font-size: 0.88rem;
  }

  .sales-page :deep(.p-inputnumber-input) {
    font-size: 0.85rem;
    padding: 0.25rem 0.4rem;
  }

  .cart-price-input :deep(.p-inputnumber-input) {
    padding-inline: 0.875rem;
  }
}
</style>
